export default {
    title: 'Components/AmwalWidget',
    parameters: {
        notes: require('./readme.md')
    },
    argTypes: {
        config: {
            control: { type: 'object' },
            description: 'Widget configuration object'
        },
        isActive: {
            control: { type: 'boolean' },
            description: 'Enable or disable the widget'
        },
        locale: {
            options: ['en', 'ar'],
            control: { type: 'select' },
            description: 'Language locale'
        },
        debug: {
            control: { type: 'boolean' },
            description: 'Enable debug mode for console logs'
        },
        customText: {
            control: { type: 'text' },
            description: 'Custom text to override the default promotional message'
        },
        // Environment selector
        environment: {
            options: ['dev', 'qa', 'prod'],
            control: { type: 'select' },
            description: 'Select environment for promotion URL'
        },
        // Enhancement Features
        showGreenBorder: {
            control: { type: 'boolean' },
            description: 'Show green border for special offers'
        },
        showMonthlyPayment: {
            control: { type: 'boolean' },
            description: 'Display monthly payment amount'
        },
        showSavings: {
            control: { type: 'boolean' },
            description: 'Show savings compared to credit card interest'
        },
        creditCardInterestRate: {
            control: { type: 'number', min: 0, max: 0.1, step: 0.01 },
            description: 'Credit card monthly interest rate for savings calculation'
        },
        showSocialProof: {
            control: { type: 'boolean' },
            description: 'Show social proof messaging'
        },
        socialProofText: {
            control: { type: 'text' },
            description: 'Custom social proof text'
        },
        showPopularBadge: {
            control: { type: 'boolean' },
            description: 'Show popular choice badge'
        },
        enableAnalytics: {
            control: { type: 'boolean' },
            description: 'Enable analytics tracking'
        },
        layoutTheme: {
            options: ['mobile', 'desktop'],
            control: { type: 'select' },
            description: 'Layout theme for the modal (defaults to mobile for better mobile-first experience)',
            defaultValue: 'mobile'
        }
    },
};

// Environment URL mapping
const getPromotionUrl = (environment) => {
    const urls = {
        dev: 'https://dev-pay.sa.amwal.tech/installment-promotion',
        qa: 'https://qa-pay.sa.amwal.tech/installment-promotion',
        prod: 'https://pay.sa.amwal.tech/installment-promotion'
    };
    return urls[environment] || urls.dev;
};

// Template using HTML with property binding
const HTMLTemplate = (args) => {
    const uniqueId = `widget-container-${Math.random().toString(36).substr(2, 9)}`;

    // Create config with dynamic promotion URL based on environment
    const configWithPromotion = {
        ...args.config,
        promotionUrl: getPromotionUrl(args.environment || 'dev'),
        // layoutTheme can be set in config or as a separate prop
        layoutTheme: args.config?.layoutTheme || args.layoutTheme || 'mobile'
    };

    return `
        <div id="${uniqueId}"></div>
        <script>
            (function() {
                try {
                    const container = document.getElementById('${uniqueId}');
                    if (!container) {
                        console.error('Container not found:', '${uniqueId}');
                        return;
                    }
                    
                    const widget = document.createElement('amwal-widget');
                    
                    // Set properties (not attributes)
                    widget.config = ${JSON.stringify(configWithPromotion)};
                    widget.isActive = ${args.isActive};
                    widget.locale = "${args.locale}";
                    widget.debug = ${args.debug};
                    widget.showGreenBorder = ${args.showGreenBorder || false};
                    widget.showMonthlyPayment = ${args.showMonthlyPayment || false};
                    widget.showSavings = ${args.showSavings || false};
                    widget.creditCardInterestRate = ${args.creditCardInterestRate || 0.02};
                    widget.showSocialProof = ${args.showSocialProof || false};
                    widget.showPopularBadge = ${args.showPopularBadge || false};
                    widget.enableAnalytics = ${args.enableAnalytics || false};
                    widget.layoutTheme = "${args.layoutTheme}";
                    
                    ${args.customText ? `widget.customText = "${args.customText}";` : ''}
                    ${args.socialProofText ? `widget.socialProofText = "${args.socialProofText}";` : ''}
                    
                    // Add event listeners
                    widget.addEventListener('promotionClick', (event) => {
                        console.log('🎉 Promotion clicked!', event.detail);
                    });
                    
                    widget.addEventListener('priceUpdate', (event) => {
                        console.log('💰 Price updated!', event.detail);
                    });
                    
                    widget.addEventListener('analyticsEvent', (event) => {
                        console.log('📊 Analytics Event:', event.detail);
                    });
                    
                    container.appendChild(widget);
                    
                    if (${args.debug}) {
                        console.log('AmwalWidget initialized successfully with:', {
                            containerId: '${uniqueId}',
                            environment: '${args.environment || 'dev'}',
                            promotionUrl: '${configWithPromotion.promotionUrl}',
                            layoutTheme: widget.layoutTheme,
                            config: widget.config,
                            isActive: widget.isActive,
                            locale: widget.locale,
                            customText: widget.customText,
                            showEnhancements: {
                                showGreenBorder: widget.showGreenBorder,
                                showMonthlyPayment: widget.showMonthlyPayment,
                                showSavings: widget.showSavings,
                                showSocialProof: widget.showSocialProof,
                                showPopularBadge: widget.showPopularBadge
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error initializing AmwalWidget:', error);
                }
            })();
        </script>
    `;
};

// Story 1: Clean Default Version
export const Default = HTMLTemplate.bind({});
Default.args = {
    config: {
        installmentsCount: 24,
        price: 10000,
        currency: 'SAR',
        locale: 'en'
    },
    isActive: true,
    locale: 'en',
    debug: true,
    environment: 'dev',
    layoutTheme: 'mobile'
};

// Story 2: Arabic Locale
export const ArabicLocale = HTMLTemplate.bind({});
ArabicLocale.args = {
    config: {
        installmentsCount: 4,
        price: 10000,
        currency: 'SAR',
        locale: 'ar'
    },
    isActive: true,
    locale: 'ar',
    debug: true,
    environment: 'dev',
    layoutTheme: 'mobile'
};

// Story 6: Custom Text
export const CustomText = HTMLTemplate.bind({});
CustomText.args = {
    config: {
        installmentsCount: 12,
        price: 10000,
        currency: 'SAR',
        locale: 'en'
    },
    isActive: true,
    locale: 'en',
    debug: true,
    customText: 'Pay in 12 easy installments with zero interest!',
    showSocialProof: true,
    environment: 'dev',
    layoutTheme: 'mobile'
};

// Story 3: Interactive
export const Interactive = HTMLTemplate.bind({});
Interactive.args = {
    config: {
        installmentsCount: 24,
        price: 10000,
        currency: 'SAR',
        locale: 'en'
    },
    isActive: true,
    locale: 'en',
    debug: true,
    showMonthlyPayment: true,
    showSavings: false,
    showSocialProof: true,
    socialProofText: 'Trusted by 50,000+ customers',
    creditCardInterestRate: 0.02,
    environment: 'dev',
    layoutTheme: 'mobile'
};

// Story 4: Special Offer Mode
export const SpecialOfferMode = HTMLTemplate.bind({});
SpecialOfferMode.args = {
    config: {
        installmentsCount: 4,
        price: 10000,
        currency: 'SAR',
        locale: 'en'
    },
    isActive: true,
    locale: 'en',
    debug: true,
    showGreenBorder: true,
    showMonthlyPayment: true,
    showSavings: false,
    environment: 'stage',
    layoutTheme: 'mobile'
};

// Story 5: Full Features Mode
export const FullInteractiveMode = HTMLTemplate.bind({});
FullInteractiveMode.args = {
    config: {
        installmentsCount: 24,
        price: 10000,
        currency: 'SAR',
        locale: 'en'
    },
    isActive: true,
    locale: 'en',
    debug: true,
    showGreenBorder: true,
    showPopularBadge: true,
    showMonthlyPayment: true,
    showSavings: false,
    showSocialProof: true,
    socialProofText: '⭐ Trusted by 50,000+ happy customers',
    enableAnalytics: true,
    creditCardInterestRate: 0.025,
    environment: 'prod',
    layoutTheme: 'mobile'
};

// Story 7: Arabic Full Interactive
export const ArabicFullInteractive = HTMLTemplate.bind({});
ArabicFullInteractive.args = {
    config: {
        installmentsCount: 24,
        price: 10000,
        currency: 'SAR',
        locale: 'ar'
    },
    isActive: true,
    locale: 'ar',
    debug: true,
    showGreenBorder: true,
    showMonthlyPayment: true,
    showSavings: false,
    showSocialProof: true,
    socialProofText: 'موثوق من قبل أكثر من 50,000 عميل',
    showPopularBadge: true,
    environment: 'prod',
    layoutTheme: 'mobile'
};